import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import IncompleteRetrievalDialog from './IncompleteRetrievalDialog';
import { OpenInViewerContext } from './openInViewerContext';
import { pendingReopenRequestState } from './pendingReopenAtom';
import { useOpenInViewerDownload } from './useOpenInViewerDownload';

const OPEN_DIALOG_TITLE_ID = 'connector-viewer-open-title';

/**
 * Provides "open in viewer" to the worklist and renders the retrieval
 * progress and incomplete-retrieval dialogs. Mounted above the patient panel
 * so closing it cannot destroy a retrieval in flight.
 */
export default function OpenInViewerDownloader({
    children,
}: {
    children: ReactNode;
}) {
    const {
        open,
        phase,
        progress,
        cancel,
        incompleteRetrieval,
        resolveIncompleteRetrieval,
    } = useOpenInViewerDownload();
    const intl = useIntl();
    const [pendingReopen, setPendingReopen] = useRecoilState(
        pendingReopenRequestState
    );

    // Consume-and-clear a reopen request parked by the save conflict
    // dialog's discard-and-reload: the same standard open flow runs, just
    // triggered by the arrival on the worklist instead of a click.
    useEffect(() => {
        if (!pendingReopen) {
            return;
        }
        setPendingReopen(null);
        open(pendingReopen.patientId, 'all');
    }, [pendingReopen, setPendingReopen, open]);

    const dialogOpen = phase === 'resolving' || phase === 'downloading';
    const percent =
        progress.total > 0
            ? Math.round((progress.settled / progress.total) * 100)
            : 0;

    return (
        <OpenInViewerContext.Provider value={open}>
            {children}
            <Dialog
                open={dialogOpen}
                aria-labelledby={OPEN_DIALOG_TITLE_ID}
                onClose={(_event, reason) => {
                    // Escape cancels; a stray backdrop click must not silently
                    // abandon a running download.
                    if (reason === 'escapeKeyDown') {
                        cancel();
                    }
                }}
            >
                <DialogTitle id={OPEN_DIALOG_TITLE_ID}>
                    {intl.formatMessage({
                        id:
                            phase === 'downloading'
                                ? 'patientList.open.loadingTitle'
                                : 'patientList.open.preparingTitle',
                    })}
                </DialogTitle>
                <DialogContent>
                    {phase === 'downloading' ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={1}
                            width={280}
                        >
                            <LinearProgress
                                variant="determinate"
                                value={percent}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {intl.formatMessage(
                                    { id: 'patientList.open.progress' },
                                    {
                                        settled: progress.settled,
                                        total: progress.total,
                                    }
                                )}
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            display="flex"
                            justifyContent="center"
                            width={280}
                            py={1}
                        >
                            <CircularProgress size={24} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancel} size="small" color="inherit">
                        {intl.formatMessage({ id: 'patientList.open.cancel' })}
                    </Button>
                </DialogActions>
            </Dialog>
            <IncompleteRetrievalDialog
                state={incompleteRetrieval}
                onResolve={resolveIncompleteRetrieval}
                action="open"
            />
        </OpenInViewerContext.Provider>
    );
}
